import React, { useEffect, useState } from "react";

import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  useMediaQuery,
  useTheme,
  Drawer,
  IconButton,
  Box,
  TextField,
  Divider,
  Stack,
  CircularProgress,
} from "@mui/material";
import DecoratingClassSessionForm from "./DecoratingClassSessionForm";

import CloseIcon from "@mui/icons-material/Close";
import api from "../../../services/api";
import { useSnackbarAlert } from "../../../hooks/useSnackbarAlert";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const AdminDecoratingClassesPage = () => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [refNum, setRefNum] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const snackbar = useSnackbarAlert();
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    const res = await api.get("/classes/sessions/upcoming?admin=true");
    setSessions(res.data);
  };

  const fetchBookings = async (sessionId) => {
    try {
      const res = await api.get(
        `/admin/classes/sessions/${sessionId}/bookings`
      );
      setBookings(res.data);
    } catch (err) {
      console.error("Failed to fetch bookings", err);
    }
  };

  const handleDrawerOpen = async (session) => {
    setSelectedSession(session);
    setDrawerOpen(true);
    await fetchBookings(session._id);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setTimeout(() => {
      setSelectedSession(null);
      setEditingId(null);
      setRefNum("");
      setBookings([]);
    }, 300);
  };

  const handleSave = async (bookingId) => {
    try {
      await api.put(`/admin/classes/bookings/${bookingId}`, {
        "payment.status": "confirmed",
        "payment.referenceNumber": refNum,
        "payment.receivedAt": new Date().toISOString(),
      });

      snackbar.success("Payment marked as received");

      setEditingId(null);
      setRefNum("");

      const updated = bookings.map((b) =>
        b._id === bookingId
          ? {
              ...b,
              payment: {
                ...b.payment,
                status: "confirmed",
                referenceNumber: refNum,
                receivedAt: new Date().toISOString(),
              },
            }
          : b
      );
      setBookings(updated);
    } catch (err) {
      console.error("Failed to update payment status", err);
      snackbar.error("Failed to update payment status");
    }
  };

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Decorating Class Sessions
      </Typography>

      <Button
        variant="contained"
        onClick={() => setCreateDrawerOpen(true)}
        sx={{ mb: 3 }}>
        Create a Session
      </Button>

      <Grid container spacing={3}>
        {sessions.map((s) => {
          const seatsFilled = s.bookings.reduce(
            (sum, b) => sum + b.numSeats,
            0
          );
          const isFull = seatsFilled >= s.capacity;
          return (
            <Grid item xs={12} md={6} key={s._id}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold">
                    {dayjs(s.date).format("MMMM D, YYYY")} @ {s.startTime}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}>
                    Location: 16 Stavanger Dr.
                  </Typography>
                  <Typography variant="body2" sx={{ my: 1 }}>
                    Capacity: {seatsFilled} / {s.capacity}
                  </Typography>
                  <Chip
                    label={isFull ? "Full" : "Open"}
                    color={isFull ? "error" : "success"}
                    size="small"
                  />
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={() => handleDrawerOpen(s)}>
                    View Signups
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            // width: isMobile ? "100%" : 500,
            p: 3,
            // maxWidth: "100%",
          },
        }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            Bookings for{" "}
            {selectedSession &&
              dayjs(selectedSession.date).format("MMMM D, YYYY")}{" "}
            @ {selectedSession?.startTime}
          </Typography>
          <IconButton onClick={handleDrawerClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        {bookings.length === 0 ? (
          <CircularProgress />
        ) : (
          <Stack spacing={3}>
            {bookings.map((b) => (
              <Box key={b._id} sx={{ overflowWrap: "anywhere" }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {b.firstName} {b.lastName}
                </Typography>
                <Typography variant="body2">{b.email}</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {b.phone}
                </Typography>
                <Typography variant="body2">Seats: {b.numSeats}</Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={b.payment.status}
                    color={
                      b.payment.status === "confirmed" ? "success" : "warning"
                    }
                    size="small"
                  />
                  {b.payment.referenceNumber && (
                    <Typography variant="caption" display="block">
                      Ref: {b.payment.referenceNumber}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ mt: 1 }}>
                  {editingId === b._id ? (
                    <Box display="flex" alignItems="center">
                      <TextField
                        size="small"
                        value={refNum}
                        onChange={(e) => setRefNum(e.target.value)}
                        placeholder="Ref #"
                        sx={{ mr: 1 }}
                      />
                      <Button size="small" onClick={() => handleSave(b._id)}>
                        Save
                      </Button>
                    </Box>
                  ) : (
                    b.payment.status !== "confirmed" && (
                      <Button size="small" onClick={() => setEditingId(b._id)}>
                        Mark Paid
                      </Button>
                    )
                  )}
                </Box>
                <Divider sx={{ my: 2 }} />
              </Box>
            ))}
          </Stack>
        )}
      </Drawer>
      <DecoratingClassSessionForm
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        onCreated={() => {
          setCreateDrawerOpen(false);
          fetchSessions();
        }}
      />
    </Container>
  );
};

export default AdminDecoratingClassesPage;
